import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Users, Check, X, Loader2, AlertCircle } from 'lucide-react';
import { useTeam } from '../../contexts/TeamContext';

interface InviteAcceptProps {
  onComplete: () => void;
}

export function InviteAccept({ onComplete }: InviteAcceptProps) {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { acceptInvite, pendingInvites } = useTeam();
  
  const [status, setStatus] = useState<'loading' | 'found' | 'invalid' | 'expired' | 'accepted'>('loading');
  const [inviteInfo, setInviteInfo] = useState<{ email: string; role: string } | null>(null);
  const [userName, setUserName] = useState('');
  const [isAccepting, setIsAccepting] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus('invalid');
      return;
    }

    // Find the invite
    const invite = pendingInvites.find(i => i.token === token);
    
    if (!invite) {
      setStatus('invalid');
      return;
    }

    // Check if expired
    if (new Date() > new Date(invite.expiresAt)) {
      setStatus('expired');
      return;
    }

    setInviteInfo({ email: invite.email, role: invite.role });
    setStatus('found');
  }, [token, pendingInvites]);

  const handleAccept = () => {
    if (!token || !userName.trim()) return;
    
    setIsAccepting(true);
    
    const success = acceptInvite(token, userName.trim());
    
    if (success) {
      setStatus('accepted');
      // Store the user info for login
      localStorage.setItem('ltss_user', JSON.stringify({
        email: inviteInfo?.email,
        name: userName.trim(),
        id: Date.now().toString(),
        role: inviteInfo?.role
      }));
      localStorage.setItem('ltss_subscription', JSON.stringify({
        plan: 'enterprise',
        status: 'active',
        stripe_customer_id: 'team_member'
      }));
      
      setTimeout(() => {
        window.location.href = '/';
      }, 2000);
    } else {
      setStatus('invalid');
    }
    
    setIsAccepting(false);
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-slate-800 border border-slate-700 rounded-2xl p-8 shadow-xl">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center">
              <Users className="w-8 h-8 text-white" />
            </div>
          </div>

          {status === 'loading' && (
            <div className="text-center">
              <Loader2 className="w-8 h-8 text-blue-400 animate-spin mx-auto mb-4" />
              <p className="text-slate-400">Verifying invite...</p>
            </div>
          )}

          {status === 'invalid' && (
            <div className="text-center">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <X className="w-8 h-8 text-red-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Invalid Invite</h2>
              <p className="text-slate-400 mb-6">
                This invite link is invalid or has already been used.
              </p>
              <button
                onClick={onComplete}
                className="px-6 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
              >
                Go to Login
              </button>
            </div>
          )}

          {status === 'expired' && (
            <div className="text-center">
              <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-amber-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Invite Expired</h2>
              <p className="text-slate-400 mb-6">
                This invite has expired. Please ask your team admin to send a new invite.
              </p>
              <button
                onClick={onComplete}
                className="px-6 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
              >
                Go to Login
              </button>
            </div>
          )}

          {status === 'found' && inviteInfo && (
            <div className="text-center">
              <h2 className="text-xl font-bold text-white mb-2">You're Invited!</h2>
              <p className="text-slate-400 mb-6">
                You've been invited to join LTSS Analytics as a <span className="text-blue-400 font-medium">{inviteInfo.role}</span>.
              </p>
              
              <div className="bg-slate-700/50 rounded-lg p-4 mb-6 text-left">
                <p className="text-sm text-slate-400 mb-1">Email</p>
                <p className="text-white">{inviteInfo.email}</p>
              </div>

              <div className="mb-6">
                <label className="block text-sm text-slate-400 mb-2 text-left">Your Name</label>
                <input
                  type="text"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  placeholder="Enter your name"
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                />
              </div>

              <button
                onClick={handleAccept}
                disabled={!userName.trim() || isAccepting}
                className="w-full py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-medium rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isAccepting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Check className="w-5 h-5" />
                    Accept Invite
                  </>
                )}
              </button>
            </div>
          )}

          {status === 'accepted' && (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Welcome to the Team!</h2>
              <p className="text-slate-400 mb-4">
                Your account has been set up. Redirecting to dashboard...
              </p>
              <Loader2 className="w-6 h-6 text-blue-400 animate-spin mx-auto" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
